import {
	formsTableFieldsFragment,
	questsTableFieldsFragmentWithoutRecords,
	recordsTableFieldsFragment,
} from "@lyfegen-platform/graphql-schema/fragments";
import gql from "graphql-tag";

export const GET_FORM_FLEX_ENTITIES = gql`
	${formsTableFieldsFragment}

	query GetFormFlexEntities($where: FlexEntityWhere!, $options: FlexEntityOptions) {
		flexEntities(options: $options, where: { AND: [{ isDeleted: false, containerType: FORM }, $where] }) {
			...GetFormsFields
		}

		filteredFormCount: flexEntities(where: { AND: [{ isDeleted: false, containerType: FORM }, $where] }) {
			_count
		}

		allFormCount: flexEntities(where: { isDeleted: false, containerType: FORM }) {
			_count
		}
	}
`;

export const GET_FILTERED_FORM_COUNT = gql`
	query GetFilteredFormCount($where: FlexEntityWhere!) {
		filteredFormCount: flexEntities(where: { AND: [{ isDeleted: false, containerType: FORM }, $where] }) {
			_count
		}
	}
`;

export const GET_FILTERED_SIMULATION_COUNT = gql`
	query GetFilteredSimulationCount($where: SimulationWhere) {
		filteredSimulationCount: simulations(where: $where) {
			_count
		}
	}
`;

export const GET_RECORDS = gql`
	${recordsTableFieldsFragment}

	query GetRecords($where: RecordWhere!, $options: RecordOptions) {
		records(options: $options, where: $where) {
			...GetRecordsFields
		}

		allRecordCount: records {
			_count
		}

		filteredRecordCount: records(where: $where) {
			_count
		}

		GetOrganizationCaselessAgreementStatus {
			hasOnlyCaselessAgreements
			hasOnlyPatientAgreements
		}
	}
`;

export const GET_RECORD_MATCHED_CASES = gql`
	query GetRecordMatchedCases($id: ID!) {
		records(where: { id: $id }) {
			matchedCaseCount
		}
	}
`;

export const GET_FILTERED_RECORD_COUNT = gql`
	query GetFilteredRecordCount($where: RecordWhere!) {
		filteredRecordCount: records(where: $where) {
			_count
		}
	}
`;

export const CAN_DELETE_FLEX_ENTITIES = gql`
	query CanDeleteFlexEntities($ids: [String!]!) {
		CanDeleteFlexEntities(input: $ids) {
			deletableResult {
				id
				deletable
			}
			impacts {
				kind
				transactionIds
			}
		}
	}
`;

export const REVOKE_RECORD = gql`
	mutation RevokeRecord($id: ID!) {
		RevokeRecord(id: $id)
	}
`;

export const GET_FORM_FILTERS = gql`
	query GetFormsFilters {
		GetFormsTableSourceFilterData {
			patientIds
			brandNames
			statuses
			addedByNames
			minAddedOn
			maxAddedOn
			types
			therapyDecisions
			occurrences
			ids
			externalFormIds
		}
		filters: GetUserContextFiltersDataForms
	}
`;

export const GET_SIMULATION_FILTERS = gql`
	query GetSimulationsFilters {
		GetSimulationsTableSourceFilterData {
			ids
			products
			shortNames
			domains
			counterparties
			models
			lastUpdatedOn
			addedByNames
			statuses
		}
		filters: GetUserFiltersDataSimulations
	}
`;

export const GET_COMPARISON_SIMULATIONS = gql`
	query GetComparisonSimulations {
		simulations: GetUserComparisonSimulations
	}
`;

export const GET_RECORD_FILTERS = gql`
	query GetRecordsFilters {
		GetRecordsTableSourceFilterData {
			purchaseOrPatientIds
			brandNames
			statuses
			addedByNames
			minAddedOn
			maxAddedOn
			minRecordDate
			maxRecordDate
			types
			occurrences
			ids
			caseIds
		}
		GetOrganizationCaselessAgreementStatus {
			hasOnlyCaselessAgreements
			hasOnlyPatientAgreements
		}
		filters: GetUserContextFiltersDataRecords
	}
`;

export const GET_QUEST_FILTERS = gql`
	query GetQuestsFilters {
		GetQuestsTableSourceFilterData {
			brandNames
			patientIds
			questOwners
			recordIds
			amountRanges {
				start
				end
				count
			}
			quantityRanges {
				start
				end
				count
			}
			minAdministrationDate
			maxAdministrationDate
		}
		filters: GetUserContextFiltersDataQuests
	}
`;

export const UPDATE_FORM_FILTER = gql`
	mutation UpdateUserContextFiltersDataForms($req: String) {
		UpdateUserContextFiltersDataForms(req: $req)
	}
`;

export const UPDATE_SIMULATION_FILTER = gql`
	mutation UpdateUsertFiltersSimulations($req: String) {
		UpdateUserFiltersSimulations(req: $req)
	}
`;

export const UPDATE_COMPARISON_SIMULATIONS = gql`
	mutation UpdateUserComparisonSimulations($req: String) {
		UpdateUserComparisonSimulations(req: $req)
	}
`;

export const UPDATE_RECORD_FILTER = gql`
	mutation UpdateUserContextFiltersDataRecords($req: String) {
		UpdateUserContextFiltersDataRecords(req: $req)
	}
`;

export const UPDATE_REPORTING_TIME_PERIOD_FILTER = gql`
	mutation UpdateUserContextTimePeriodFilterReporting($agreementId: ID!, $req: String) {
		UpdateUserContextTimePeriodFilterReporting(agreementId: $agreementId, req: $req)
	}
`;

export const UPDATE_QUEST_FILTER = gql`
	mutation UpdateUserContextFiltersDataQuests($req: String) {
		UpdateUserContextFiltersDataQuests(req: $req)
	}
`;

export const FORMS_SUBSCRIPTION = gql`
	subscription OnFormCreated {
		formCreated
	}
`;

export const GET_USER_CONTEXT_TIME_PERIOD_FILTER_REPORTING = gql`
	query GetUserContextTimePeriodFilterReporting($agreementId: ID!) {
		filters: GetUserContextTimePeriodFilterReporting(agreementId: $agreementId)
	}
`;

export const RECORDS_SUBSCRIPTION = gql`
	subscription OnRecordUpdated {
		recordUpdated
	}
`;

export const VERIFY_RECORD_IN_QUEST = gql`
	mutation VerifyRecordsInQuest($recordsIds: [ID!], $userId: ID!) {
		VerifyRecordsInQuest(recordsIds: $recordsIds, userId: $userId)
	}
`;

export const UNVERIFY_RECORD_IN_QUEST = gql`
	mutation UnverifyRecordsInQuest($recordsIds: [ID!], $userId: ID!) {
		UnverifyRecordsInQuest(recordsIds: $recordsIds, userId: $userId)
	}
`;

export const FLEX_ENTITY_UPLOAD_SUBSCRIPTION = gql`
	subscription OnFlexEntityUpload {
		flexEntityUploading {
			isFinal
			status
			uploadedById
			tenantId
			percentage
			casesCreated
			patientsCreated
			expectedRunAgreements {
				id
				name
			}
			completedAgreementIds
		}
	}
`;

export const FLEX_ENTITY_BULK_SUBSCRIPTION = gql`
	subscription OnFlexEntityBulk {
		flexEntityBulk {
			tenantId
			expectedRunAgreements
			percentage
			uploadedById
			casesCreated
			patientsCreated
			formsCreated
		}
	}
`;

export const GET_QUESTS = gql`
	${questsTableFieldsFragmentWithoutRecords}

	query GetQuestTableRows($where: QuestTableRowWhere, $options: QuestTableRowOptions) {
		questTableRows(where: $where, options: $options) {
			...GetQuestsFieldsWithoutRecords
		}

		filteredQuestCount: questTableRows(where: $where) {
			_count
		}

		allQuestCount: questTableRows(where: $where) {
			_count
		}
	}
`;

export const SIMULATION_SUBSCRIPTION = gql`
	subscription OnSimulationUpdated {
		simulationUpdated
	}
`;

export const GET_SIMULATIONS = gql`
	query GetSimulations($where: SimulationWhere!, $options: SimulationOptions) {
		simulations(where: $where, options: $options) {
			id
			formattedContent
			shortName
			domainName
			simulationUserRole
			indications
			businessCase
			counterparty
			product
			model
			lastUpdatedOn
			createdByEmail
			createdByName
			status
			isSimulationComplete
			hasUnassignedBlueprint
			blueprint {
				_dbNodeId
				status
			}
		}

		allSimulationCount: simulations {
			_count
		}

		filteredSimulationCount: simulations(where: $where) {
			_count
		}
	}
`;

export const GET_FILTERED_QUEST_COUNT = gql`
	query GetFilteredQuestCount($where: QuestTableRowWhere) {
		filteredQuestCount: questTableRows(where: $where) {
			_count
		}
	}
`;

export const GET_RECORDS_VERIFIER = gql`
	query GetRecordById($ids: [ID!]) {
		records(where: { id_IN: $ids }) {
			id
			verifiedBy {
				firstName
				lastName
			}
		}
	}
`;

export const GET_QUEST_BY_ID = gql`
	query GetQuestById($id: ID!) {
		quests(where: { id: $id }) {
			recordsRelationship {
				id
				data
				status
				createdOn
				createdByName
				createdByEmail
				occurrence
				preparationId
				preparationGenericNames
				preparationBrandNames
				insuranceId
				matchedCaseCount
				type
				financialDataPoints {
					generatedFor {
						flexEntitiesConnection(where: { node: { type: { isStartingPoint: true } } }) {
							edges {
								node {
									createdBy {
										firstName
										lastName
									}
								}
							}
						}
					}
				}
				verifiedBy {
					firstName
					lastName
				}
			}
		}
	}
`;

export const GET_CREATE_BUTTON_DATA = gql`
	query getAgreementAllowedForms($containerType: FlexContainerType!, $requiredCreationWay: DataContainerCreationWay!) {
		flexEntityTypes(where: { containerType: $containerType, creationWays_INCLUDES: $requiredCreationWay }) {
			id
			isStartingPoint
			name
			localizationKey
			createdOn
		}
		baseAgreements(where: { isDeleted: false, isHidden: false }) {
			allowedForms
			allowedRecords
			dataContainers {
				providers {
					id
					name
				}
			}
		}
	}
`;

export const GET_COVERAGE_BUTTON_DATA = gql`
	query getAgreementAllowedFormsForQuests(
		$containerType: FlexContainerType!
		$requiredCreationWay: DataContainerCreationWay!
		$preparationBrandNames: [String!]
	) {
		flexEntityTypes(where: { containerType: $containerType, creationWays_INCLUDES: $requiredCreationWay }) {
			id
			isStartingPoint
			name
			localizationKey
			createdOn
		}
		baseAgreements(
			where: {
				isDeleted: false
				isHidden: false
				enrollingConditionSets_SOME: {
					scopes_SOME: { treatmentSets_SOME: { preparations_SOME: { BrandName_IN: $preparationBrandNames } } }
				}
			}
		) {
			allowedForms
			allowedRecords
			dataContainers {
				providers {
					id
					name
				}
			}
		}
	}
`;

export const GET_POTENTIALLY_ALLOWED_ENTITY_TYPES = gql`
	query GetPotentiallyAllowedEntityTypes($containerType: FlexContainerType!) {
		flexEntityTypes(where: { containerType: $containerType }) {
			id
			isStartingPoint
			name
			localizationKey
			createdOn
		}

		baseAgreements(where: { isDeleted: false, isHidden: false }) {
			allowedForms
			allowedRecords
			dataContainers {
				providers {
					id
					name
				}
			}
		}
	}
`;

export const GET_UPLOAD_BUTTON_DATA = gql`
	query GetAgreementAllowedRecords {
		baseAgreements(where: { status_IN: [IN_APPROVAL, APPROVED, RUNNING] }) {
			allowedRecords
		}
	}
`;

const catalogueAttributeFieldsFragment = gql`
	fragment CatalogueAttributeFieldsFragment on CatalogueAttribute {
		id
		identifier
		type
		dataType
		dataSubType
		validation
		minLength
		maxLength
		arrayPossible
		values
		hasDependant {
			id
		}
		dependsOn {
			id
			identifier
		}
		mutuallyRelevant {
			id
		}
	}
`;

export const GET_FLEX_ENTITY_UPLOAD_ENTITY_TYPE = gql`
	${catalogueAttributeFieldsFragment}

	query GetFlexEntityUploadEntityType($entityTypeId: ID!) {
		flexEntityTypes(where: { id: $entityTypeId }) {
			id
			idPrefix
			isStartingPoint
			name
			localizationKey
			data
			orgDataMappings
			catalogueAttributes {
				...CatalogueAttributeFieldsFragment
			}
			recordTypes {
				id
				catalogueAttributes {
					...CatalogueAttributeFieldsFragment
				}
			}
		}

		catalogueAttributes(where: { id: "DAT0001" }) {
			...CatalogueAttributeFieldsFragment
		}

		uploadLimitations {
			entityType
			maxItems
		}
	}
`;

export const GET_USER_FIELDS_FOR_UPLOAD = gql`
	query GetUserFieldsForUpload($entityType: FlexContainerType!, $file: FileUpload) {
		GetUserFieldsForUpload(entityType: $entityType, file: $file)
	}
`;

export const GET_IS_SOURCE_KEYS_UNIQUE = gql`
	query GetIsSourceKeysUnique($components: [String!]!) {
		GetIsSourceKeysUnique(components: $components)
	}
`;
